<script setup lang="ts">
import Translator from "@/utils/common/Translator.ts";
import {computed, ref, useTemplateRef} from "vue";
import {useRouter} from "vue-router";
import TopPanelSearchUtils, {FieldToFocusSearchItem, SearchItem} from "@/utils/common/TopPanelSearchUtils.ts";
import NavBarSearchInEntitySelector from "@/components/inputs/NavBarSearchInEntitySelector.vue";
import SearchIcon from "@/components/icons/navBarIcons/SearchIcon.vue";
import FilterListIcon from "@/components/icons/navBarIcons/FilterListIcon.vue";
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import BlockUtils from "@/utils/common/BlockUtils.ts";
import type {NamaIconProps} from "@/utils/presenters/interfaces.ts";
import {QSelect} from "quasar";
import SideBarUtils from "@/utils/common/SideBarUtils.ts";
import LinkIcon from "@/assets/widgetIcons/LinkIcon.vue";
import IdsAndConstants from "@/utils/UIConstants/IdsAndConstants.ts";

const {compactMode} = storeToRefs(useNamaFlagsStore());
let model = ref<string | null>(null);
let options = ref<SearchItem[]>([]);
let loading = ref(false)
const router = useRouter();
const qSelectRef = useTemplateRef<QSelect>("qSelectRef")
const namaFlagStore = useNamaFlagsStore()
const isEnterKeyDown = ref(false);
const route = ref();

let searchInEntity = ref<SearchItem | null>(null);
let label = computed(() => {
  if (!searchInEntity.value)
    return undefined;
  return Translator.translateId('searchInEntity') + ' ' + Translator.translateId(searchInEntity.value.id + "s")
})

function extractCodeOrIdFromClipboardJson(val: string): string | null {
  if (!val.startsWith(IdsAndConstants.NM_JSON_PREFIX))
    return null;
  try {
    const parsed = JSON.parse(val.substring(IdsAndConstants.NM_JSON_PREFIX.length));
    return parsed?.id ?? parsed?.code ?? null;
  } catch {
    return null;
  }
}

async function filter(val: string | null | undefined) {
  if (!val) {
    options.value = [];
    return;
  }
  loading.value = true;
  const lowerVal = val.toLowerCase();
  if (lowerVal.startsWith("f:") || lowerVal.startsWith("p:") || val.startsWith("ح:") || val.startsWith("ب:")) {
    options.value = TopPanelSearchUtils.searchForFields(val.substring(2));
  } else {
    const searchText = extractCodeOrIdFromClipboardJson(val) ?? val;
    options.value = await TopPanelSearchUtils.searchFor(searchText, searchInEntity.value?.id);
  }
  loading.value = false;
}

function onFilter(val, update) {
  update(async () => {
      await filter(val)
    },
    newRef => {
      if (val !== '' && options.value.length > 0) {
        setTimeout(() => {
          newRef.setOptionIndex(-1) // reset optionIndex in case there is something selected
          newRef.moveOptionSelection(1, true) // focus the first selectable option and do not update the input-value
        });
      }
    })
}

function setModel(val) {
  model.value = val;
}

function onSelect(value: SearchItem) {
  route.value = TopPanelSearchUtils.resolveSearchItemRoute(value);
  if (!route.value && value.text) {
    namaFlagStore.setFieldToFocusOptions({field: value as FieldToFocusSearchItem})
    qSelectRef.value?.blur()
  }
  if (isEnterKeyDown.value && route.value) {
    onClick(undefined);
    isEnterKeyDown.value = false;
  }
  model.value = null;
}

function focusSearchInSelector() {
  if (document.getElementsByClassName('header-search-in-popup-input').length)
    (document.getElementsByClassName('header-search-in-popup-input')[0] as HTMLElement).focus();
}

function formatFieldLocation(searchItem: FieldToFocusSearchItem) {
  const slash = Translator.isEnglish() ? " / " : " \\ "
  let {pageTitle, block, blockId, presenter} = searchItem.record?.extraData
  let blockType = "Block"
  if (BlockUtils.blockIsEditScreenGroup(block))
    blockType = "Group"
  if (BlockUtils.blockIsEditScreenGrid(block))
    blockType = "Grid"
  const pageText = pageTitle ? `<span>${Translator.translateId('Page')}(</span><b>${Translator.translateId(pageTitle)}</b ><span>)</span>` : ''
  const groupText = blockId ? `<b>${slash}</b><span>${Translator.translateId(blockType)}(</span><b>${Translator.translateTitledId(presenter.entityType, block) || blockId}</b><span>)</span>` : ''
  return pageText + groupText
}

const groupDefaultIconCode: Record<string, string> = {
  "EditScreenGrid": "nm-layout",
  "EditScreenActionsBlock": "nm-actions",
  "EditScreenGroup": "nm-input"
}

function resolveNamaIconProps(searchItem: SearchItem): NamaIconProps {
  const defaultIcon = "nm-monitoring"
  if (searchItem.type !== 'text')
    return {
      defaultIcon,
      constant: searchItem.id
    }
  const extraData = searchItem.record?.extraData!
  const block = extraData.block
  return {
    block: extraData.field,
    presenter: extraData.presenter,
    defaultIcon: groupDefaultIconCode[block['namaClassTypeId']] ?? defaultIcon
  }
}

function onKeyDown(event: KeyboardEvent) {
  const qSelect = qSelectRef.value;
  if (!qSelect)
    return;
  if (event.key === 'Escape') {
    qSelect.blur();
  }
  if (options.value.length === 0)
    return;
  if (event.key === 'ArrowDown') {
    const currentIndex = qSelect.getOptionIndex();
    const lastIndex = options.value.length - 1;
    if (currentIndex === lastIndex)
      qSelect.setOptionIndex(-1);
  } else if (event.key === 'ArrowUp') {
    const currentIndex = qSelect.getOptionIndex();
    if (currentIndex === 0)
      qSelect.setOptionIndex(options.value.length + 1);
  }
}

function onItemClick(e: Event | MouseEvent) {
  onClick(e);
}

function onEnter(e: Event | MouseEvent) {
  isEnterKeyDown.value = true;
}

function onClick(e: Event | MouseEvent | undefined, value?: SearchItem) {
  if (!route.value) {
    if (value)
      onSelect(value);
    else
      return;
  }
  if (SideBarUtils.shouldOpenInNewTab(e) || value) {
    window.open(router.resolve(route.value).href, "_blank");
  } else {
    router.push(route.value);
  }
  route.value = undefined;
}

</script>

<template>
  <div class="tw-mb-0" id="header-search-container">
    <div class="tw-relative tw-leading-tight">
      <q-select
        filled use-input hide-selected fill-input dense
        :options-dense="compactMode"
        hide-dropdown-icon
        input-debounce="0"
        behavior="menu"
        ref="qSelectRef"
        :model-value="model"
        :loading="loading"
        :options="options"
        option-label="text"
        @filter="onFilter"
        @input-value="setModel"
        @update:model-value="onSelect"
        @keydown="onKeyDown"
        @keydown.enter="onEnter"
        :label="label"
        :placeholder="searchInEntity?'':Translator.translateId('searchByNameOrCode')"
        class="nama-customize-dense-SearchField tw-rounded tw-bg-namaBackground-bright-light tw-shadow tw-appearance-none dark:tw-bg-namaBackground-bright-dark"
        input-class="tw-bg-transparent tw-px-0 tw-text-base ultra:tw-text-sm focus:tw-ring-0 focus:tw-shadow-none focus:tw-border-none dark:tw-text-white
         dark:tw-placeholder-gray-300"
        popup-content-class="navbar-search-popup dark:tw-bg-namaBackground-deep-dark dark:tw-text-white">
        <template v-slot:option="scope">
          <q-item v-bind="scope.itemProps" class="tw-ps-3 tw-justify-between" @click.stop="onItemClick">
            <q-item-section side>
              <div class="tw-flex tw-items-center tw-gap-2">
                <NamaIcon v-bind="{...resolveNamaIconProps(scope.opt)}" class="tw-size-5" :key="scope.opt.id"/>
                <q-item-label class="tw-text-[13px] tw-leading-none dark:tw-text-white">
                  <div v-text="scope.opt.text"
                       class="tw-text-namaFontColor-input-light/70 dark:tw-text-white/70 tw-font-semibold tw-text-[13px]"/>
                  <div v-if="scope.opt.type==='text'" v-html="formatFieldLocation(scope.opt)"
                       class="tw-text-[11px] [&>span]:!tw-opacity-60 [&>b]:tw-text-namaFontColor-input-light/50 dark:[&>b]:tw-text-white"/>
                </q-item-label>
              </div>
            </q-item-section>
            <q-item-section side>
              <q-avatar square class="nama-avatar-hover tw-size-7 tw-cursor-pointer"
                        :title="Translator.translateId('openInNewWindow')">
                <a @click.stop="(event) => onClick(event, scope.opt)" tabindex="-1">
                  <LinkIcon class="tw-w-5 tw-h-5 !tw-fill-primary-light dark:!tw-fill-primary-dark"/>
                </a>
              </q-avatar>
            </q-item-section>
          </q-item>
        </template>
        <template v-slot:prepend>
          <q-avatar square @click.stop.prevent class="tw-size-7 ultra:tw-size-6">
            <SearchIcon class="tw-w-5 ultra:tw-w-4"/>
          </q-avatar>
        </template>
        <template v-slot:append>
          <q-avatar square @click.stop.prevent class="tw-cursor-pointer tw-relative tw-size-7 ultra:tw-size-6">
            <FilterListIcon class="tw-h-5 ultra:tw-h-4"/>
            <div class="tw-absolute tw--top-1/4 tw-right-2/3 tw-text-red-400" v-if="searchInEntity">*</div>
            <q-popup-edit v-model="searchInEntity" v-slot="scope" auto-save anchor="bottom right" class="dark:tw-bg-namaBackground-deep-dark"
                          @show="focusSearchInSelector">
              <div class="text-italic text-primary-light">
                {{ Translator.translateId('entityTypeDF') }}
              </div>
              <NavBarSearchInEntitySelector v-model="scope.value"/>
            </q-popup-edit>
          </q-avatar>
        </template>
      </q-select>
    </div>
  </div>
</template>
<style>
.navbar-search-popup .q-item.q-item-type {
  @apply dark:tw-border-b-[1px] dark:tw-border-b-[#6C757D] dark:tw-border-solid tw-border-b-[1px] tw-border-b-gray-300 tw-border-solid
}

.tw-dark .navbar-search-popup {
  scrollbar-color: #8b8b8b #2c2c2c;
}

#header-search-container .q-field--labeled .q-field__label {
  @apply dark:tw-text-gray-300
}
</style>
